import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete, 
  Business, 
  Refresh, 
  Warning, 
  ChevronLeft, 
  ChevronRight,
  Image,
  Public,
  Star,
  StarBorder,
  Category
} from '@mui/icons-material';
import { 
  getBrands, 
  createBrand, 
  updateBrand, 
  deleteBrand, 
  getBrandStatistics 
} from '../../services/brand';
import { categoryService } from '../../services/categoryService';
import CategoryTreeSelector from '../../components/CategoryTreeSelector';

const defaultForm = {
  name: '',
  description: '',
  logo: null,
  country: '',
  is_official: false,
  is_generic: false,
  categories_ids: []
};

const Brands = () => {
  // Local state management
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalBrands, setTotalBrands] = useState(0);
  const [categories, setCategories] = useState([]);
  
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, brandId: null, brandName: '' });
  
  // Filter states for dynamic search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  
  // Pagination states
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Calculate total pages from totalBrands
  const totalPages = Math.ceil(totalBrands / rowsPerPage) || 1;
  
  // Category selection state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  
  // Logo preview state
  const [logoPreview, setLogoPreview] = useState({ open: false, url: '', name: '' });

  // Load categories
  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const response = await categoryService.getCategoryTree();
      console.log('Category tree API response:', response);
      if (response.success) {
        const categoriesData = response.data || [];
        console.log('Category tree data:', categoriesData);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } else {
        console.error('Failed to load category tree:', response.error);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error loading category tree:', error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch brands with filters
  const fetchBrandsData = useCallback(async (pageArg, rowsArg, filterParams = {}) => {
    console.log('🚀 fetchBrandsData called:', { pageArg, rowsArg, filterParams });
    
    setLoading(true);
    setError(null);
    
    try {
      // Only include non-empty filters
      const cleanFilters = {};
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value && value.trim() !== '' && value !== 'all') {
          cleanFilters[key] = value;
        }
      });

      console.log('🚀 Fetching brands with:', { cleanFilters, page: pageArg + 1, page_size: rowsArg });
      
      const response = await getBrands({
        ...cleanFilters,
        page: pageArg + 1, // backend expects 1-based
        page_size: rowsArg,
        ordering: 'name',
      });
      
      console.log('🚀 Brands API response:', response);
      
      if (response.success) {
        setBrands(response.data || []);
        setTotalBrands(response.count || 0);
      } else {
        setError(response.error || 'Failed to fetch brands');
        setBrands([]);
        setTotalBrands(0);
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError(err.message || 'Failed to fetch brands');
      setBrands([]);
      setTotalBrands(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    fetchBrandsData(0, rowsPerPage, {});
    loadCategories();
  }, [rowsPerPage, loadCategories, fetchBrandsData]);

  // Handle pagination changes
  useEffect(() => {
    console.log('🔍 Page effect triggered:', { page, rowsPerPage });
    if (page > 0) { // Only fetch if not initial load
      fetchBrandsData(page, rowsPerPage, {
        search: searchTerm,
        status: statusFilter,
        country: countryFilter
      });
    }
  }, [page, rowsPerPage, fetchBrandsData, searchTerm, statusFilter, countryFilter]);

  // Handle filter changes
  useEffect(() => {
    console.log('🔍 Filter effect triggered:', { searchTerm, statusFilter, countryFilter, rowsPerPage });
    setPage(0); // Reset to first page when filters change
    fetchBrandsData(0, rowsPerPage, {
      search: searchTerm,
      status: statusFilter,
      country: countryFilter
    });
  }, [searchTerm, statusFilter, countryFilter, rowsPerPage, fetchBrandsData]);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value);
  };

  const handleCustomRowsInputBlur = () => {
    const newRowsPerPage = parseInt(customRowsInput, 10);
    if (newRowsPerPage > 0 && newRowsPerPage <= 1000) {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle category selection from tree selector
  const handleCategorySelect = (category) => {
    const isAlreadySelected = selectedCategories.some(cat => cat.id === category.id);
    
    if (isAlreadySelected) {
      // Remove category if already selected
      const updatedCategories = selectedCategories.filter(cat => cat.id !== category.id);
      setSelectedCategories(updatedCategories);
      setForm(prev => ({
        ...prev,
        categories_ids: updatedCategories.map(cat => cat.id)
      }));
    } else {
      // Add category if not selected
      const updatedCategories = [...selectedCategories, category];
      setSelectedCategories(updatedCategories);
      setForm(prev => ({
        ...prev,
        categories_ids: updatedCategories.map(cat => cat.id)
      }));
    }
    setCategoryModalOpen(false);
  };

  // Handle opening category selection modal
  const handleOpenCategoryModal = () => {
    setCategoryModalOpen(true);
  };

  // Handle closing category selection modal
  const handleCloseCategoryModal = () => {
    setCategoryModalOpen(false);
  };

  // Handle file upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFormChange('logo', file);
    }
  };

  // Handle logo preview
  const handleLogoPreview = (logoUrl, brandName) => {
    setLogoPreview({
      open: true,
      url: logoUrl,
      name: brandName
    });
  };

  // Reset form
  const resetForm = () => {
    setForm(defaultForm);
    setSelectedCategories([]);
    setEditId(null);
  };

  // Handle dialog open
  const handleOpen = (brand = null) => {
    if (brand) {
      setForm({
        name: brand.name || '',
        description: brand.description || '',
        logo: null,
        country: brand.country || '',
        is_official: brand.is_official || false,
        is_generic: brand.is_generic || false,
        categories_ids: brand.categories?.map(cat => cat.sid) || []
      });
      
      // Set selected categories for display
      const brandCategories = categories.filter(cat => 
        brand.categories?.some(brandCat => brandCat.sid === cat.sid)
      );
      setSelectedCategories(brandCategories);
      setEditId(brand.sid);
    } else {
      resetForm();
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('country', form.country);
      formData.append('is_official', form.is_official);
      formData.append('is_generic', form.is_generic);
      
      if (form.logo) {
        formData.append('logo', form.logo);
      }
      
      // Add categories - send as JSON string to avoid FormData object issue
      formData.append('categories_ids', JSON.stringify(form.categories_ids));

      let response;
      if (editId) {
        response = await updateBrand(editId, formData);
        setSnackbar({ open: true, message: 'Brand updated successfully!', severity: 'success' });
      } else {
        response = await createBrand(formData);
        setSnackbar({ open: true, message: 'Brand created successfully!', severity: 'success' });
      }
      
      console.log('Brand operation response:', response);
      
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error saving brand:', error);
      
      // Extract specific error message from API response
      let errorMessage = 'Failed to save brand';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Handle validation errors
        if (errorData.name && Array.isArray(errorData.name)) {
          errorMessage = errorData.name[0]; // Get first validation error
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setLoading(true);
    
    try {
      await deleteBrand(deleteModal.brandId);
      setSnackbar({ open: true, message: 'Brand deleted successfully!', severity: 'success' });
      setDeleteModal({ open: false, brandId: null, brandName: '' });
      handleRefresh();
    } catch (error) {
      console.error('Error deleting brand:', error);
      
      // Extract specific error message from API response
      let errorMessage = 'Failed to delete brand';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = useCallback((searchValue) => {
    setSearchTerm(searchValue);
  }, []);

  // Handle status filter
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  // Handle country filter
  const handleCountryFilter = (e) => {
    setCountryFilter(e.target.value);
  };

  // Handle refresh
  const handleRefresh = () => {
    setPage(0);
    fetchBrandsData(0, rowsPerPage, {
      search: searchTerm,
      status: statusFilter,
      country: countryFilter
    });
  };

  // Use brands directly since filtering is done server-side
  const filteredBrands = brands;

  // Get unique countries for filter
  const countries = useMemo(() => {
    const countrySet = new Set(brands.map(brand => brand.country).filter(Boolean));
    return Array.from(countrySet).sort();
  }, [brands]);

  // DataGrid columns
  const columns = [
    {
      field: 'id',
      headerName: '#',
      width: 60,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const index = brands.findIndex(brand => brand.id === params.value);
        return index + 1 + (page * rowsPerPage);
      }
    },
    {
      field: 'logo',
      headerName: 'Logo',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const logoUrl = params.value || params.row.logo_url;
        return (
          <Tooltip title="Click to preview logo">
            <Avatar
              src={logoUrl}
              alt={params.row.name}
              sx={{ 
                width: 40, 
                height: 40, 
                cursor: logoUrl ? 'pointer' : 'default',
                '&:hover': logoUrl ? { transform: 'scale(1.1)' } : {}
              }}
              onClick={logoUrl ? () => handleLogoPreview(logoUrl, params.row.name) : undefined}
            >
              <Business />
            </Avatar>
          </Tooltip>
        );
      },
      sortable: false,
      filterable: false
    },
    {
      field: 'name',
      headerName: 'Brand Name',
      width: 200,
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          icon={<Public />}
          label={params.value || 'N/A'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'categories',
      headerName: 'Categories',
      width: 200,
      headerAlign: 'center',
      renderCell: (params) => {
        const categories = params.value || [];
        const categoryNames = categories.map(cat => cat.name).join(', ');
        
        return (
          <Box sx={{ maxWidth: 180 }}>
            {categoryNames ? (
              <Tooltip title={categoryNames}>
                <Typography variant="body2" noWrap>
                  {categoryNames}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No categories
              </Typography>
            )}
          </Box>
        );
      },
      sortable: false
    },
    {
      field: 'is_official',
      headerName: 'Official',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          icon={params.value ? <Star /> : <StarBorder />}
          label={params.value ? 'Official' : 'Unofficial'}
          size="small"
          color={params.value ? 'primary' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'is_generic',
      headerName: 'Generic',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isGeneric = params.value;
        const isOfficial = params.row.is_official;
        
        let label, color, variant;
        
        if (isGeneric) {
          label = 'Unbranded';
          color = 'secondary';
          variant = 'filled';
        } else if (isOfficial) {
          label = 'Branded';
          color = 'primary';
          variant = 'filled';
        } else {
          label = 'Branded';
          color = 'default';
          variant = 'outlined';
        }
        
        return (
          <Chip
            label={label}
            size="small"
            color={color}
            variant={variant}
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Typography variant="caption">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Brand">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ color: 'primary.main' }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Brand">
            <IconButton
              size="small"
              onClick={() => setDeleteModal({ 
                open: true, 
                brandId: params.row.id, 
                brandName: params.row.name 
              })}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading && brands.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading brands...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Brand Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            Add Brand
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Brands"
              placeholder="Search by name, description, or country..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilter}
              >
                <MenuItem value="all">All Brands</MenuItem>
                <MenuItem value="official">Official Only</MenuItem>
                <MenuItem value="generic">Generic Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={countryFilter}
                label="Country"
                onChange={handleCountryFilter}
              >
                <MenuItem value="all">All Countries</MenuItem>
                {countries.map(country => (
                  <MenuItem key={country} value={country}>{country}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {totalBrands} brands
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ backgroundColor: 'background.paper' }}>
        <Box sx={{ 
          width: '100%',
          overflowX: 'auto',
          minHeight: 520,
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8a8a8',
          }
        }}>
          {filteredBrands.length === 0 && !loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
              <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                No brands found
              </Typography>
              <Button variant="outlined" onClick={() => fetchBrandsData(0, rowsPerPage, {})}>
                Refresh Data
              </Button>
            </Box>
          ) : (
            <DataGrid
              rows={filteredBrands}
              columns={columns}
              loading={loading}
              pageSize={rowsPerPage}
              rowsPerPageOptions={[]}
              disableSelectionOnClick
              hideFooter
              getRowId={(row) => {
                // Ensure we always have a unique ID
                if (row.id) return row.id;
                if (row.name) return row.name;
                // Fallback to a combination of properties if neither id nor name exists
                return `${row.description || 'brand'}_${row.country || 'unknown'}_${Date.now()}`;
              }}
              sx={{
                border: 'none',
                minWidth: 1200, // Ensure minimum width for all columns
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                }
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? 'Edit Brand' : 'Add New Brand'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Brand Name"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Country"
                  value={form.country}
                  onChange={(e) => handleFormChange('country', e.target.value)}
                  placeholder="e.g., USA, Japan, South Korea"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Categories
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleOpenCategoryModal}
                    disabled={categoriesLoading}
                    startIcon={<Category />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {categoriesLoading ? "Loading categories..." : "Select Categories"}
                  </Button>
                  {selectedCategories.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {selectedCategories.map((category) => (
                        <Chip
                          key={category.id}
                          label={category.name}
                          variant="outlined"
                          onDelete={() => handleCategorySelect(category)}
                          color="primary"
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_official}
                        onChange={(e) => handleFormChange('is_official', e.target.checked)}
                      />
                    }
                    label="Official Brand"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_generic}
                        onChange={(e) => handleFormChange('is_generic', e.target.checked)}
                      />
                    }
                    label="Generic Brand"
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Brand Logo
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Image />}
                    fullWidth
                  >
                    {form.logo ? 'Change Logo' : 'Upload Logo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </Button>
                  {form.logo && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Selected: {form.logo.name}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>
              {editId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModal.open} onClose={() => setDeleteModal({ open: false, brandId: null, brandName: '' })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the brand "{deleteModal.brandName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal({ open: false, brandId: null, brandName: '' })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logo Preview Dialog */}
      <Dialog 
        open={logoPreview.open} 
        onClose={() => setLogoPreview({ open: false, url: '', name: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Image />
            Logo Preview - {logoPreview.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <img
              src={logoPreview.url}
              alt={logoPreview.name}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Box
              sx={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: 'text.secondary'
              }}
            >
              <Image sx={{ fontSize: 48, mb: 1 }} />
              <Typography>Logo not available</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoPreview({ open: false, url: '', name: '' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      )}

      {/* Category Selection Modal */}
      <CategoryTreeSelector
        open={categoryModalOpen}
        onClose={handleCloseCategoryModal}
        onSelect={handleCategorySelect}
        categories={categories}
        selectedCategories={selectedCategories}
        title="Select Categories for Brand"
        allowMultiple={true}
        showUseButton={true}
      />
    </Box>
  );
};

export default Brands;
